import React from 'react';
import type { Vehicle } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { getVehicleBrandLabel } from '@hero/hooks-shared';
import { cn } from '@hero/web-lib';
import { VehicleDynamicIcon, UserIcon, CheckIcon } from '@hero/web-icons';
import {
  AvailabilityChip,
  formatPlateForDisplay,
  vehicleStatusChip,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';
import { MianAvatar } from '../../../app/MianAvatar';

export interface VehicleCardSelectableProps {
  vehicle: Vehicle;
  isSelected: boolean;
  onSelect: (vehicle: Vehicle) => void;
}

export const VehicleCardSelectable: React.FC<VehicleCardSelectableProps> = ({
  vehicle,
  isSelected,
  onSelect,
}) => {
  const { t } = useTranslation(['m-vehicle']);

  const chip = vehicleStatusChip(t, vehicle.status);
  const brandLabel = getVehicleBrandLabel(vehicle.classification.brand, t);
  const typeLabel = t(`types.${vehicle.classification.type}`, {
    defaultValue: vehicle.classification.type,
  });
  const brandYear = `${brandLabel}${
    vehicle.classification.year ? ` · ${vehicle.classification.year}` : ''
  }`;
  const driver = vehicle.driver;
  const driverLine = driver ? `${driver.name} · ${driver.phone}` : t('selector.no_driver');

  return (
    <button
      type="button"
      onClick={() => onSelect(vehicle)}
      className={cn(
        'relative w-full rounded-xl border-2 p-3 text-start transition-all',
        'hover:shadow-md hover:border-primary/40',
        isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-gray-200 bg-white',
      )}>
      {isSelected && (
        <div className="absolute top-2 end-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
          <CheckIcon className="h-3 w-3 text-white" />
        </div>
      )}

      <div className="flex gap-3 rtl:flex-row-reverse">
        <MianAvatar
          src={vehicle.images?.[0]?.url ?? ''}
          className="size-14 shrink-0 rounded-lg"
          emptyFallback={
            <VehicleDynamicIcon
              vehicle={vehicle.classification.type}
              size="small"
              className="size-8 text-gray-700"
            />
          }
        />

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="min-w-0 truncate text-sm font-medium text-gray-900" dir="auto">
              {formatPlateForDisplay(vehicle.registration.plate)}
            </span>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <VehicleDynamicIcon
                vehicle={vehicle.classification.type}
                className="h-3.5 w-3.5 shrink-0"
              />
              <span className="truncate">{typeLabel}</span>
              <span className="text-gray-300">·</span>
              <span className="truncate">{brandYear}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2 text-xs text-gray-600">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100">
                <UserIcon className="size-3.5 text-gray-600" size="small" />
              </span>
              <span className="truncate">{driverLine}</span>
            </div>
          </div>
          <AvailabilityChip label={chip.label} tone={chip.tone} />
        </div>
      </div>
    </button>
  );
};
